<template>
  <div
    class="absolute h-full transition-left-300 group-scope"
    :class="[
      showSidebar ? 'left-0' : isPaperShape ? '-left-323' : '-left-310',
      { 'mt-60': showUpgradeToProBar && !isMobile },
      {'mt-150': showUpgradeToProBar && isMobile }
    ]"
  >
    <div
      v-if="showBackdrop"
      class="fixed top-0 z-9998 w-full h-full"
      :class="[
        {'left-323' : isBookPage},
        {'left-268' : isBookPage},
        {'left-56': isToolPage}
      ]"
      @click="close"
    />

    <div
      v-if="!isBooksPage"
      class="anim-opacity opacity-0 group-scope-hover:opacity-100"
    >
      <ButtonModule
        prefix="icon-sidebar text-11"
        class="hidden md:block absolute top-0  m-5 p-10 pt-5 rounded-8 opacity-25 anim-all hover:opacity-100"
        :class="[
          isBookPage || isBooksPage ? 'right-0' : 'left-50',
          isMagenta ? 'text-dark-600' : 'text-app-color',
          isDarkMode ? 'hover:bg-dark-500' : 'hover:bg-menu-hover',
          {'ml-10': isPaperShape}
        ]"
        @click="toggleSidebar"
      />
    </div>

    <div class="h-full flex" :class="isBooksPage ? 'w-268' : 'w-323'">
      <MainSidebar
        v-if="!isBooksPage"
        :show-instructions="projectInstructionsActiveFirstStep"
      />

      <div id="sidebar" class="flex flex-col anim-all"
           :class="[
             isBookPage || isBooksPage ? 'w-268' : 'w-40',
             sidebarClasses, {'border-r bg-primary': isHeadquarters && isProjectInstructionsAccepted}, borderColor
           ]"
      >
        <LogoModule v-if="isBooksPage" class="flex flex-shrink-0 text-24 tracking-negative mb-10 md:mb-0 pl-25 pt-18 md:pl-30" />

        <div v-if="isHeadquarters" class="flex mt-50">
          <div class="flex mx-auto">
            <img v-if="teamLogo" class="rounded-6 max-w-100 max-h-80 w-full h-full object-contain" :src="teamLogo" alt="image">

            <label
              v-if="isAdmin && !teamLogo"
              class="w-125 h-115 rounded-14 mx-auto border-3 border-dashed border-grey-700 flex opacity-90 hover:opacity-100 cursor-pointer"
              :class="isDarkMode ? 'bg-book': 'bg-grey-200'"
            >
              <p class="text-15 text-grey-700 m-auto">Upload logo</p>
              <input type="file" class="hidden" @change="uploadLogo">
            </label>
          </div>
        </div>

        <div class="overflow-hidden mt-20">
          <Projects v-if="isBookPage" />
        </div>
      </div>
    </div>
    <SettingsModal />
  </div>
</template>

<script>
import LogoModule from '@/components/shared/LogoModule'
import SettingsModal from '@/components/settings/SettingsModal'
import Projects from '@/components/projects/Projects'
import { teamsService } from '@/services'

import { mapGetters, mapMutations, mapState } from 'vuex'
import MainSidebar from '@/components/layout/MainSidebar'
import ButtonModule from '@/components/shared/form-controls/ButtonModule'

export default {
  name: 'HomeSidebar',

  components: {
    LogoModule,
    SettingsModal,
    Projects,
    ButtonModule,
    MainSidebar
  },

  props: {
    showUpgradeToProBar: Boolean
  },

  emits: ['toggleSidebar'],

  computed: {
    ...mapGetters(['isMobile', 'isDarkMode', 'hasTeam', 'isMagenta', 'isAdmin', 'isPaperShape']),

    ...mapState({
      settings: state => state.settings.settings,
      isProjectInstructionsAccepted: state => state.profile.user.profile.showProjectOnboarding,
      projectInstructionsActiveStep: state => state.profile.activeStep,
      user: state => state.profile.user,
      team: state => state.team.team,
      showSidebar: state => state.settings.showSidebar,
      isPoseidonTheme: state => state.settings.settings.theme === 'Poseidon'
    }),

    showBackdrop () {
      return this.showSidebar && this.isMobile
    },

    projectInstructionsActiveFirstStep () {
      return !this.isProjectInstructionsAccepted && this.projectInstructionsActiveStep === 1 && this.isBookPage
    },

    teamLogo () {
      return this.team?.teamLogo?.url
    },

    isBooksPage () {
      return this.$route.name === 'Books' || this.$route.name === 'TeamBooks'
    },

    isBookPage () {
      return this.$route.name === 'Book' || this.$route.name === 'TeamBook' || this.isHeadquarters
    },

    isToolPage () {
      return ['TaskBoard', 'TaskBoardTeam', 'WeekPlanner', 'WeekPlannerTeam', 'Discussions'].includes(this.$route.name)
    },

    sidebarClasses () {
      let classes

      if (this.isProjectInstructionsAccepted) {
        if (this.isHeadquarters) return 'bg-primary'
        if (this.isToolPage && !this.isPaperShape) return 'bg-canvas'

        if (this.isPoseidonTheme) {
          classes = `${this.isBookPage && !this.isToolPage ? 'bg-primary' : 'bg-transparent'}`
        } else {
          classes = this.isBooksPage ? 'bg-transparent' : 'bg-book'
        }
      }

      return classes
    },

    isHeadquarters () {
      return this.$route.name === 'Headquarters'
    },

    borderColor () {
      return this.isDarkMode ? 'border-hover' : 'border-grey-500'
    }
  },

  methods: {
    ...mapMutations(['updateSidebar', 'setLogo']),

    close () {
      if (this.isMobile) this.updateSidebar(false)
    },

    uploadLogo (event) {
      const hasFiles = event.target.files.length

      if (!hasFiles) return

      const files = Array
        .from(event.target.files)
        .filter(file => (/image/i).test(file.type))

      if (files.length === 0) return

      const formData = new FormData()
      formData.append('data', files[0])

      return teamsService.upload(formData)
        .then(res => {
          this.setLogo(res.data)
        })
    },

    toggleSidebar () {
      this.$emit('toggleSidebar')
    }
  }
}
</script>
